import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import {
    Store, Upload, Image, Save, X, Phone, Mail, MapPin,
    Clock, Truck, RotateCcw, FileText, Eye, EyeOff, Loader, Camera,
    Check, AlertCircle, DollarSign
} from 'lucide-react';

const SellerStoreSettings = ({ userInfo }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    
    const [storeData, setStoreData] = useState({
        storeName: '',
        storeDescription: '',
        storeLogo: '',
        storeBanner: '',
        storePhone: '',
        storeEmail: '',
        storeAddress: '',
        businessHours: '',
        shippingPolicy: '',
        returnPolicy: '',
        minOrderAmount: '',
        freeShippingAmount: '',
        shippingFee: '',
        estimatedDelivery: '2-3일',
        storeVisible: true, // 스토어 공개 여부
        bankName: '',        // 은행명
        bankAccount: '',     // 계좌번호
        bankHolder: '',      // 예금주
    });

    const loadStoreData = useCallback(async () => {
        if (!userInfo?.uid) return;
        
        try {
            setLoading(true);
            const userDoc = await getDoc(doc(db, 'users', userInfo.uid));
            
            if (userDoc.exists()) {
                const data = userDoc.data();
                setStoreData({
                    storeName: data.storeName || data.businessName || '',
                    storeDescription: data.storeDescription || '',
                    storeLogo: data.storeLogo || '',
                    storeBanner: data.storeBanner || '',
                    storePhone: data.storePhone || data.phone || '',
                    storeEmail: data.storeEmail || data.email || '',
                    storeAddress: data.storeAddress || data.address || '',
                    businessHours: data.businessHours || '평일 09:00 - 18:00',
                    shippingPolicy: data.shippingPolicy || '',
                    returnPolicy: data.returnPolicy || '',
                    minOrderAmount: data.minOrderAmount || '',
                    freeShippingAmount: data.freeShippingAmount || '',
                    shippingFee: data.shippingFee || '',
                    estimatedDelivery: data.estimatedDelivery || '2-3일',
                    storeVisible: data.storeVisible ?? true, // null/undefined일 때만 true, false는 유지
                    bankName: data.bankName || '',
                    bankAccount: data.bankAccount || '',
                    bankHolder: data.bankHolder || '',
                });
            }
        } catch (error) {
            console.error('스토어 정보 로드 실패:', error);
            alert('스토어 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [userInfo]);

    // 권한 체크 및 데이터 로드
    useEffect(() => {
        if (!userInfo) {
            return; // 로딩 중일 수 있음
        }

        // 판매자 승인 체크 (businessType이 seller인 경우만)
        if (userInfo.businessType === 'seller' && userInfo.sellerStatus !== 'approved') {
            alert('판매자 승인 후 이용 가능합니다.');
            navigate('/seller-application');
            return;
        }

        loadStoreData();
    }, [userInfo, navigate, loadStoreData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setStoreData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // 파일 크기 체크 (5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('이미지 크기는 5MB 이하여야 합니다.');
            return;
        }

        // 이미지 타입 체크
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 업로드 가능합니다.');
            return;
        }

        try {
            if (type === 'logo') {
                setUploadingLogo(true);
            } else {
                setUploadingBanner(true);
            }

            const fileName = `${type}_${userInfo.uid}_${Date.now()}.${file.name.split('.').pop()}`;
            const storageRef = ref(storage, `stores/${userInfo.uid}/${fileName}`);
            
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            setStoreData(prev => ({
                ...prev,
                [type === 'logo' ? 'storeLogo' : 'storeBanner']: downloadURL
            }));

            alert(`${type === 'logo' ? '로고' : '배너'}가 업로드되었습니다.`);
        } catch (error) {
            console.error('이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다.');
        } finally {
            if (type === 'logo') {
                setUploadingLogo(false);
            } else {
                setUploadingBanner(false);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!storeData.storeName.trim()) {
            alert('스토어명을 입력해주세요.');
            return;
        }

        try {
            setSaving(true);

            await updateDoc(doc(db, 'users', userInfo.uid), {
                storeName: storeData.storeName,
                storeDescription: storeData.storeDescription,
                storeLogo: storeData.storeLogo,
                storeBanner: storeData.storeBanner,
                storePhone: storeData.storePhone,
                storeEmail: storeData.storeEmail,
                storeAddress: storeData.storeAddress,
                businessHours: storeData.businessHours,
                shippingPolicy: storeData.shippingPolicy,
                returnPolicy: storeData.returnPolicy,
                minOrderAmount: storeData.minOrderAmount ? parseInt(storeData.minOrderAmount) : 0,
                freeShippingAmount: storeData.freeShippingAmount ? parseInt(storeData.freeShippingAmount) : 0,
                shippingFee: storeData.shippingFee ? parseInt(storeData.shippingFee) : 0,
                estimatedDelivery: storeData.estimatedDelivery,
                storeVisible: storeData.storeVisible,
                bankName: storeData.bankName,
                bankAccount: storeData.bankAccount,
                bankHolder: storeData.bankHolder,
                storeUpdatedAt: serverTimestamp(),
            });

            alert('스토어 정보가 저장되었습니다.');
        } catch (error) {
            console.error('저장 실패:', error);
            alert('저장에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = () => {
        window.open(`/marketplace/seller/${userInfo.uid}`, '_blank');
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} />
                <p>스토어 정보를 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        <Store size={28} />
                        내 스토어 설정
                    </h1>
                    <p style={styles.subtitle}>
                        마켓플레이스에 표시될 스토어 정보를 설정하세요
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* 스토어 공개 설정 */}
                <div style={styles.visibilitySection}>
                    <div style={styles.visibilityContent}>
                        <div style={styles.visibilityInfo}>
                            {storeData.storeVisible ? (
                                <Eye size={24} color="#10b981" />
                            ) : (
                                <EyeOff size={24} color="#ef4444" />
                            )}
                            <div>
                                <h3 style={styles.visibilityTitle}>
                                    스토어 {storeData.storeVisible ? '공개 중' : '비공개'}
                                </h3>
                                <p style={styles.visibilityDesc}>
                                    {storeData.storeVisible 
                                        ? '마켓플레이스에서 스토어가 노출됩니다'
                                        : '마켓플레이스에서 스토어가 숨겨집니다'}
                                </p>
                            </div>
                        </div>
                        <label style={styles.toggleSwitch}>
                            <input
                                type="checkbox"
                                checked={storeData.storeVisible}
                                onChange={(e) => setStoreData(prev => ({ ...prev, storeVisible: e.target.checked }))}
                                style={styles.toggleInput}
                            />
                            <span style={{
                                ...styles.toggleSlider,
                                backgroundColor: storeData.storeVisible ? '#10b981' : '#d1d5db',
                            }}>
                                <span style={{
                                    ...styles.toggleKnob,
                                    transform: storeData.storeVisible ? 'translateX(24px)' : 'translateX(0)',
                                }}></span>
                            </span>
                        </label>
                    </div>
                </div>

                {/* 기본 정보 */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <Store size={20} />
                        기본 정보
                    </h2>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>스토어명 *</label>
                        <input
                            type="text"
                            name="storeName"
                            value={storeData.storeName}
                            onChange={handleChange}
                            placeholder="마켓에 표시될 스토어 이름"
                            style={styles.input}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>스토어 소개</label>
                        <textarea
                            name="storeDescription"
                            value={storeData.storeDescription}
                            onChange={handleChange}
                            placeholder="스토어 소개글을 입력하세요 (브랜드 스토리, 주력 상품 등)"
                            style={styles.textarea}
                            rows={4}
                        />
                    </div>
                </div>

                {/* 로고 */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <Image size={20} />
                        로고
                    </h2>

                    <div style={styles.imageRow}>
                        {/* 로고 */}
                        <div style={styles.imageUploadBox}>
                            <label style={styles.label}>스토어 로고</label>
                            <div style={styles.logoPreview}>
                                {storeData.storeLogo ? (
                                    <img src={storeData.storeLogo} alt="로고" style={styles.logoImage} />
                                ) : (
                                    <div style={styles.logoPlaceholder}>
                                        <Camera size={32} color="#94a3b8" />
                                        <span>200 x 200</span>
                                    </div>
                                )}
                                <label style={styles.uploadOverlay}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageUpload(e, 'logo')}
                                        style={{ display: 'none' }}
                                        disabled={uploadingLogo}
                                    />
                                    {uploadingLogo ? (
                                        <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                                    ) : (
                                        <Upload size={20} />
                                    )}
                                </label>
                            </div>
                            <p style={styles.imageHint}>권장: 200x200px, 5MB 이하</p>
                        </div>

                    </div>
                </div>

                {/* 연락처 정보 */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <Phone size={20} />
                        연락처 정보
                    </h2>

                    <div style={styles.formRow}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                <Phone size={16} /> 전화번호
                            </label>
                            <input
                                type="tel"
                                name="storePhone"
                                value={storeData.storePhone}
                                onChange={handleChange}
                                placeholder="02-1234-5678"
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                <Mail size={16} /> 이메일
                            </label>
                            <input
                                type="email"
                                name="storeEmail"
                                value={storeData.storeEmail}
                                onChange={handleChange}
                                placeholder="store@example.com"
                                style={styles.input}
                            />
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            <MapPin size={16} /> 주소
                        </label>
                        <input
                            type="text"
                            name="storeAddress"
                            value={storeData.storeAddress}
                            onChange={handleChange}
                            placeholder="서울시 강남구 테헤란로 123"
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            <Clock size={16} /> 영업시간
                        </label>
                        <input
                            type="text"
                            name="businessHours"
                            value={storeData.businessHours}
                            onChange={handleChange}
                            placeholder="평일 09:00 - 18:00 (주말/공휴일 휴무)"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* 배송 정책 */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <Truck size={20} />
                        배송 정책
                    </h2>

                    <div style={styles.formRow}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>기본 배송비 (원)</label>
                            <input
                                type="number"
                                name="shippingFee"
                                value={storeData.shippingFee}
                                onChange={handleChange}
                                placeholder="3000"
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>무료배송 기준금액 (원)</label>
                            <input
                                type="number"
                                name="freeShippingAmount"
                                value={storeData.freeShippingAmount}
                                onChange={handleChange}
                                placeholder="50000"
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>예상 배송기간</label>
                            <select
                                name="estimatedDelivery"
                                value={storeData.estimatedDelivery}
                                onChange={handleChange}
                                style={styles.select}
                            >
                                <option value="1일">1일</option>
                                <option value="1-2일">1-2일</option>
                                <option value="2-3일">2-3일</option>
                                <option value="3-5일">3-5일</option>
                                <option value="5-7일">5-7일</option>
                                <option value="1-2주">1-2주</option>
                            </select>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>배송 안내</label>
                        <textarea
                            name="shippingPolicy"
                            value={storeData.shippingPolicy}
                            onChange={handleChange}
                            placeholder="배송 관련 상세 안내 (배송사, 도서산간 추가비용 등)"
                            style={styles.textarea}
                            rows={3}
                        />
                    </div>
                </div>

                {/* 정산 계좌 */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <DollarSign size={20} />
                        정산 계좌
                    </h2>
                    <p style={{ fontSize: '13px', color: '#64748b', marginTop: '-12px', marginBottom: '16px' }}>
                        무통장입금 주문 시 구매자에게 표시되는 계좌 정보입니다.
                    </p>

                    <div style={styles.formRow}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>은행명</label>
                            <select
                                name="bankName"
                                value={storeData.bankName}
                                onChange={handleChange}
                                style={styles.select}
                            >
                                <option value="">은행 선택</option>
                                <option value="국민은행">국민은행</option>
                                <option value="신한은행">신한은행</option>
                                <option value="우리은행">우리은행</option>
                                <option value="하나은행">하나은행</option>
                                <option value="농협은행">농협은행</option>
                                <option value="기업은행">기업은행</option>
                                <option value="SC제일은행">SC제일은행</option>
                                <option value="씨티은행">씨티은행</option>
                                <option value="카카오뱅크">카카오뱅크</option>
                                <option value="케이뱅크">케이뱅크</option>
                                <option value="토스뱅크">토스뱅크</option>
                                <option value="대구은행">대구은행</option>
                                <option value="부산은행">부산은행</option>
                                <option value="경남은행">경남은행</option>
                                <option value="광주은행">광주은행</option>
                                <option value="전북은행">전북은행</option>
                                <option value="제주은행">제주은행</option>
                                <option value="수협은행">수협은행</option>
                                <option value="새마을금고">새마을금고</option>
                                <option value="신협">신협</option>
                                <option value="우체국">우체국</option>
                            </select>
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>계좌번호</label>
                            <input
                                type="text"
                                name="bankAccount"
                                value={storeData.bankAccount}
                                onChange={handleChange}
                                placeholder="- 없이 숫자만 입력"
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>예금주</label>
                            <input
                                type="text"
                                name="bankHolder"
                                value={storeData.bankHolder}
                                onChange={handleChange}
                                placeholder="예금주명"
                                style={styles.input}
                            />
                        </div>
                    </div>
                </div>

                {/* 교환/반품 정책 */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <RotateCcw size={20} />
                        교환/반품 정책
                    </h2>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>교환/반품 안내</label>
                        <textarea
                            name="returnPolicy"
                            value={storeData.returnPolicy}
                            onChange={handleChange}
                            placeholder={`교환/반품 정책을 입력하세요

예시:
- 상품 수령 후 7일 이내 교환/반품 가능
- 단순 변심에 의한 반품 시 왕복 배송비 고객 부담
- 상품 하자 시 무료 교환/반품
- 개봉 후 사용한 상품은 교환/반품 불가`}
                            style={styles.textarea}
                            rows={6}
                        />
                    </div>
                </div>

                {/* 저장 버튼 */}
                <div style={styles.buttonGroup}>
                    <button
                        type="button"
                        style={styles.cancelBtn}
                        onClick={() => navigate('/seller-dashboard')}
                    >
                        취소
                    </button>
                    <button
                        type="submit"
                        style={styles.saveBtn}
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                                저장 중...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                저장하기
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* 안내 박스 */}
            <div style={styles.infoBox}>
                <AlertCircle size={20} color="#3b82f6" />
                <div>
                    <strong>스토어 설정 안내</strong>
                    <ul style={styles.infoList}>
                        <li>스토어명과 로고는 마켓플레이스에서 브랜드로 표시됩니다</li>
                        <li>배너는 스토어 페이지 상단에 표시됩니다</li>
                        <li>배송/반품 정책은 상품 상세페이지에 표시됩니다</li>
                        <li>변경사항은 저장 후 즉시 반영됩니다</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '24px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        gap: '16px',
        color: '#64748b',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#64748b',
        margin: '8px 0 0 0',
    },
    previewBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#f1f5f9',
        color: '#475569',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    visibilitySection: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '2px solid #e2e8f0',
    },
    visibilityContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
    },
    visibilityInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    visibilityTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 4px 0',
    },
    visibilityDesc: {
        fontSize: '13px',
        color: '#64748b',
        margin: 0,
    },
    toggleSwitch: {
        position: 'relative',
        display: 'inline-block',
        cursor: 'pointer',
    },
    toggleInput: {
        opacity: 0,
        width: 0,
        height: 0,
        position: 'absolute',
    },
    toggleSlider: {
        display: 'block',
        width: '52px',
        height: '28px',
        borderRadius: '14px',
        transition: 'background-color 0.3s',
        position: 'relative',
    },
    toggleKnob: {
        position: 'absolute',
        top: '2px',
        left: '2px',
        width: '24px',
        height: '24px',
        backgroundColor: 'white',
        borderRadius: '50%',
        transition: 'transform 0.3s',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    section: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 20px 0',
        paddingBottom: '12px',
        borderBottom: '1px solid #e2e8f0',
    },
    formGroup: {
        marginBottom: '20px',
        flex: 1,
    },
    formRow: {
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
        marginBottom: '8px',
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box',
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        backgroundColor: 'white',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        fontFamily: 'inherit',
        resize: 'vertical',
        boxSizing: 'border-box',
    },
    imageRow: {
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap',
    },
    imageUploadBox: {
        flex: 1,
        minWidth: '200px',
    },
    logoPreview: {
        position: 'relative',
        width: '150px',
        height: '150px',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '2px dashed #e2e8f0',
        backgroundColor: '#f8fafc',
    },
    logoImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    logoPlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        color: '#94a3b8',
        fontSize: '12px',
    },
    bannerPreview: {
        position: 'relative',
        width: '100%',
        height: '150px',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '2px dashed #e2e8f0',
        backgroundColor: '#f8fafc',
    },
    bannerImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    bannerPlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        color: '#94a3b8',
        fontSize: '12px',
    },
    uploadOverlay: {
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        width: '36px',
        height: '36px',
        backgroundColor: 'white',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        color: '#6366f1',
    },
    imageHint: {
        fontSize: '12px',
        color: '#94a3b8',
        marginTop: '8px',
    },
    buttonGroup: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '32px',
    },
    cancelBtn: {
        padding: '14px 28px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    saveBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '14px 32px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    infoBox: {
        display: 'flex',
        gap: '16px',
        padding: '20px',
        backgroundColor: '#eff6ff',
        borderRadius: '12px',
        marginTop: '24px',
    },
    infoList: {
        margin: '8px 0 0 0',
        paddingLeft: '20px',
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.8',
    },
};

export default SellerStoreSettings;